import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown, Instagram, MapPin } from "lucide-react";
import { MaskedLine } from "@/components/Reveal";
import { scrollToSection } from "@/lib/scroll";
import { PHONE_DISPLAY, PHONE_TEL, ADDRESS_LINE, MAPS_PROFILE_URL, INSTAGRAM_URL } from "@/data/menu";

const HERO_BG = "/assets/baianeira/baianeira-interior.jpeg";

export const Hero = ({ discount = 20, promoEndDate = "" }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="grain relative flex min-h-[calc(100svh-19rem)] flex-col justify-end overflow-hidden md:min-h-[calc(100svh-21rem)]"
    >
      <motion.div
        style={{ y: bgY }}
        initial={{ scale: 1.15 }}
        animate={{ scale: 1 }}
        transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1] }}
        className="absolute inset-0 z-0"
      >
        <img
          src={HERO_BG}
          alt="Fogliame tropicale"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 z-[1] bg-gradient-to-b from-black/70 via-black/45 to-[#050505]" />

      <div className="relative z-[2] mx-auto w-full max-w-6xl px-5 pb-14 pt-10 md:px-8 md:pb-24 md:pt-12">
        <MaskedLine delay={0.05}>
          <div className="mb-4 flex justify-center md:mb-6">
            <span
              data-testid="promo-limited-badge"
              className="inline-flex max-w-full flex-wrap items-center justify-center gap-2.5 border border-[#D4AF37]/60 bg-[#D4AF37]/10 px-4 py-2.5 text-center text-[9px] font-bold leading-relaxed tracking-[0.2em] text-[#F3E5AB] md:text-[10px]"
            >
              <span className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-[#D4AF37]" />
              POSTI LIMITATI PER IL {discount}% DI SCONTO · UN'OFFERTA CHE NON È SEMPRE DISPONIBILE
            </span>
          </div>
          <p
            data-testid="hero-price-anchor"
            className="mt-4 font-display text-2xl font-semibold text-[#F5F2E9] md:mt-5 md:text-4xl"
          >
            Media 35€ a persona
          </p>
        </MaskedLine>
        <MaskedLine delay={0.15}>
          <p className="mb-2 text-[10px] font-semibold tracking-[0.4em] text-[#D4AF37] md:mb-3 md:text-xs">
            ROMA · CINECITTÀ · METRO SUBAUGUSTA
          </p>
        </MaskedLine>
        <MaskedLine delay={0.22}>
          <p className="mb-5 text-[10px] tracking-[0.25em] text-[#E0E0E0]/85 md:mb-7">
            {ADDRESS_LINE.toUpperCase()} ·{" "}
            <a
              data-testid="hero-phone-link"
              href={PHONE_TEL}
              className="font-semibold text-[#F3E5AB] underline-offset-4 hover:underline"
            >
              {PHONE_DISPLAY}
            </a>
          </p>
        </MaskedLine>

        <MaskedLine delay={0.26}>
          <div className="flex flex-wrap items-center gap-3">
            <a
              data-testid="hero-maps-button"
              href={MAPS_PROFILE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-[#D4AF37] bg-[#050505]/75 px-4 py-3 text-[11px] font-bold tracking-[0.08em] text-[#F3E5AB] shadow-lg transition-colors duration-300 hover:bg-[#D4AF37] hover:text-[#050505] sm:px-5 sm:tracking-[0.15em] md:text-[12px]"
            >
              <MapPin size={16} /> GOOGLE MAPS
            </a>
            <a
              data-testid="hero-instagram-button"
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram Baianeira"
              className="inline-flex items-center gap-2 border border-[#D4AF37] bg-[#050505]/75 px-4 py-3 text-[11px] font-bold tracking-[0.08em] text-[#F3E5AB] shadow-lg transition-colors duration-300 hover:bg-[#D4AF37] hover:text-[#050505] sm:px-5 sm:tracking-[0.15em] md:text-[12px]"
            >
              <Instagram size={16} /> INSTAGRAM
            </a>
          </div>
        </MaskedLine>

        <h1 className="font-display text-[clamp(2.4rem,10vw,5.5rem)] font-medium leading-[1.02] text-[#F5F2E9]">
          <MaskedLine delay={0.3}>IL TUO SUSHI,</MaskedLine>
          <MaskedLine delay={0.45}>
            <span>
              CON IL{" "}
              <em className="gold-shimmer not-italic font-semibold">
                {discount}%
              </em>{" "}
              DI
            </span>
          </MaskedLine>
          <MaskedLine delay={0.6}>
            <span>
              SCONTO{" "}
              <span
                data-testid="hero-menu-alla-carta"
                className="mt-2 block font-sans text-sm font-bold tracking-[0.3em] text-[#D4AF37] md:text-xl"
              >
                SUL MENÙ ALLA CARTA
              </span>
            </span>
          </MaskedLine>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="mt-8 flex flex-col items-start gap-5 md:mt-10 md:flex-row md:items-center md:gap-8"
        >
          <a
            data-testid="hero-cta-button"
            href="#coupon-form"
            onClick={(e) => {
              e.preventDefault();
              scrollToSection("#coupon-form");
            }}
            className="gold-glow bg-[#D4AF37] px-8 py-4 text-xs font-bold tracking-[0.25em] text-[#050505] transition-transform duration-300 hover:bg-[#F3E5AB] active:scale-[0.98] md:text-sm"
          >
            PRENOTA CON IL {discount}% DI SCONTO
          </a>

          <a
            data-testid="hero-menu-button"
            href="/menu/menu-baianeira.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="border-2 border-[#D4AF37] px-8 py-4 text-xs font-bold tracking-[0.25em] text-[#F3E5AB] transition-colors duration-300 hover:bg-[#D4AF37] hover:text-[#050505] active:scale-[0.98] md:text-sm"
          >
            VEDI IL MENÙ
          </a>
          <a
            data-testid="hero-conditions-link"
            href="#condizioni"
            onClick={(e) => {
              e.preventDefault();
              scrollToSection("#condizioni");
            }}
            className="hidden text-[10px] tracking-[0.3em] text-[#A3A3A3] underline-offset-4 transition-colors hover:text-[#D4AF37] md:block md:text-xs"
          >
            CONDIZIONI DI UTILIZZO
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="mt-12 flex items-center gap-3 text-[#D4AF37]/70 md:mt-16"
        >
          <ChevronDown size={16} className="animate-bounce" />
          <span className="text-[9px] tracking-[0.4em]">SCORRI</span>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
